import { Component, OnDestroy, OnInit } from "@angular/core";
import { Title } from "@angular/platform-browser";
import { ActivatedRoute } from "@angular/router";
import { CourseLogo } from "@app/admin/models/course-logo";
import { CourseLogosService } from "@app/admin/services/course-logos.service";
import { OperationResponse } from "@app/common/models/operation-response";
import { OnlineStatusType } from "@app/common/online-status/online-status-type.enum";
import { OnlineStatusService } from "@app/common/online-status/online-status.service";
import { StaticText } from "@app/common/static-text";
import { Subscription } from "rxjs";

@Component({
    selector: 'ebs-course-logos',
    templateUrl: './course-logos.component.html',
    styleUrls: ['./course-logos.component.scss'],
    standalone: false
})
export class CourseLogosComponent implements OnInit, OnDestroy {
  isOffline: boolean;
  theSubscription: Subscription;
  adminOfflineMessage = StaticText.adminOfflineMessage;
  courseCode: string;
  courseName: string;

  primaryTileLogo: CourseLogo;
  primaryTileLogoType: number = 1;
  primaryTileLogoSrcContent: string = "";
  primaryTileLogoLoaded: boolean = false;

  secondaryTileLogo: CourseLogo;
  secondaryTileLogoType: number = 4;
  secondaryTileLogoSrcContent: string = "";
  secondaryTileLogoLoaded: boolean = false;

  primaryCourseLogo: CourseLogo;
  primaryCourseLogoType: number = 2;
  primaryCourseLogoSrcContent: string = "";
  primaryCourseLogoLoaded: boolean = false;

  secondaryCourseLogo: CourseLogo;
  secondaryCourseLogoType: number = 3;
  secondaryCourseLogoSrcContent: string = "";
  secondaryCourseLogoLoaded: boolean = false;

  isAdminForCourse = true;

  constructor(
    private titleService: Title,
    private onlineStatusService: OnlineStatusService,
    private route: ActivatedRoute,
    private courseLogosService: CourseLogosService
  ) {
    this.onlineStatusService.isOffline().subscribe(x => {
      this.isOffline = x;
    }, error => {
      this.isOffline = false;
    });
    this.theSubscription = this.onlineStatusService.status.subscribe(
      event => {
        if (event === OnlineStatusType.OFFLINE)
          this.isOffline = true;

        if (event === OnlineStatusType.ONLINE) {
          this.isOffline = false;
        }
      }
    )
  }

  ngOnInit() {
    if (!this.isOffline)
      this.titleService.setTitle('Course logos');

    this.route.paramMap.subscribe(params => {
      this.courseCode = params.get('courseCode');
      this.courseName = this.route.snapshot.queryParams.courseName;

      this.courseLogosService.getCourseLogoAccess(this.courseCode).subscribe((response: OperationResponse<CourseLogo>) => {
        if (response != null && response.operationSucceeded) {
          this.getCourseLogos();
        }
        else if (response != null && (response as any).status === 401) {
          this.isAdminForCourse = false;
        }
      });

    });
  }

  getCourseLogos() {
    this.courseLogosService.getCourseLogo(this.courseCode, this.primaryTileLogoType).subscribe((response: OperationResponse<CourseLogo>) => {
      if (response != null && response.operationSucceeded) {
        this.setLogoData(response.data, this.primaryTileLogoType);
      }
      this.primaryTileLogoLoaded = true;
    });

    this.courseLogosService.getCourseLogo(this.courseCode, this.secondaryTileLogoType).subscribe((response: OperationResponse<CourseLogo>) => {
      if (response != null && response.operationSucceeded) {
        this.setLogoData(response.data, this.secondaryTileLogoType);
      }
      this.secondaryTileLogoLoaded = true;
    });

    this.courseLogosService.getCourseLogo(this.courseCode, this.primaryCourseLogoType).subscribe((response: OperationResponse<CourseLogo>) => {
      if (response != null && response.operationSucceeded) {
        this.setLogoData(response.data, this.primaryCourseLogoType);
      }
      this.primaryCourseLogoLoaded = true;
    });

    this.courseLogosService.getCourseLogo(this.courseCode, this.secondaryCourseLogoType).subscribe((response: OperationResponse<CourseLogo>) => {
      if (response != null && response.operationSucceeded) {
        this.setLogoData(response.data, this.secondaryCourseLogoType);
      }
      this.secondaryCourseLogoLoaded = true;
    });
  }

  onFileChange(event, logoType: number) {
    if (event.target.files && event.target.files.length > 0) {
      this.readAndUploadFile(event.target.files[0], logoType);
    }
  }

  onRemoveLogoClick(logoType: number) {
    let id: string = "";

    if (logoType === this.primaryTileLogoType) id = this.primaryTileLogo.id
    else if (logoType == this.secondaryTileLogoType) id = this.secondaryTileLogo.id
    else if (logoType === this.primaryCourseLogoType) id = this.primaryCourseLogo.id
    else if (logoType === this.secondaryCourseLogoType) id = this.secondaryCourseLogo.id

    this.courseLogosService.deleteCourseLogo(this.courseCode, id)
      .subscribe(response => {
        if (response.operationSucceeded) {
          if (logoType === this.primaryTileLogoType) {
            this.primaryTileLogo = null;
            this.primaryTileLogoSrcContent = "";
          }
          else if (logoType === this.secondaryTileLogoType) {
            this.secondaryTileLogo = null;
            this.secondaryTileLogoSrcContent = "";
          }
          else if (logoType === this.primaryCourseLogoType) {
            this.primaryCourseLogo = null;
            this.primaryCourseLogoSrcContent = "";
          }
          else if (logoType === this.secondaryCourseLogoType) {
            this.secondaryCourseLogo = null;
            this.secondaryCourseLogoSrcContent = "";
          }
        }
      });
  }

  private readAndUploadFile(theFile: any, logoType: number) {
    let file = new CourseLogo()
    file.courseLogoType = logoType;

    let reader = new FileReader();

    reader.onload = () => {
      file.fileBase64 = reader.result.toString();

      if ((logoType === this.primaryTileLogoType && this.primaryTileLogo != null) ||
        (logoType === this.secondaryTileLogoType && this.secondaryTileLogo != null) ||
        (logoType === this.primaryCourseLogoType && this.primaryCourseLogo != null) ||
        (logoType === this.secondaryCourseLogoType && this.secondaryCourseLogo != null)) {

        let id: string = "";
        if (logoType === this.primaryTileLogoType) id = this.primaryTileLogo.id
        else if (logoType == this.secondaryTileLogoType) id = this.secondaryTileLogo.id
        else if (logoType === this.primaryCourseLogoType) id = this.primaryCourseLogo.id
        else if (logoType === this.secondaryCourseLogoType) id = this.secondaryCourseLogo.id

        this.courseLogosService.updateCourseLogo(this.courseCode, id, file)
          .subscribe((response: OperationResponse<CourseLogo>) => {
            this.setLogoData(response.data, logoType);
          });
      }
      else {
        this.courseLogosService.setCourseLogo(this.courseCode, file)
          .subscribe((response: OperationResponse<CourseLogo>) => {
            this.setLogoData(response.data, logoType);
          });
      }
    }

    reader.readAsDataURL(theFile);
  }

  private setLogoData(logo: CourseLogo, logoType: number) {
    if (logoType === this.primaryTileLogoType) {
      this.primaryTileLogo = logo;
      this.primaryTileLogoSrcContent = `${logo.fileType},${logo.fileBytes}`;
    }
    else if (logoType === this.secondaryTileLogoType) {
      this.secondaryTileLogo = logo;
      this.secondaryTileLogoSrcContent = `${logo.fileType},${logo.fileBytes}`;
    }
    else if (logoType === this.primaryCourseLogoType) {
      this.primaryCourseLogo = logo;
      this.primaryCourseLogoSrcContent = `${logo.fileType},${logo.fileBytes}`;
    }
    else if (logoType === this.secondaryCourseLogoType) {
      this.secondaryCourseLogo = logo;
      this.secondaryCourseLogoSrcContent = `${logo.fileType},${logo.fileBytes}`;
    }
  }

  ngOnDestroy(): void {
    if (this.theSubscription)
      this.theSubscription.unsubscribe();
  }
}